import { OFFICE_365_DEFAULT_REDIRECT } from '@my-backoffice/app/pages/login/federated/office365/constants/office365-auth.constants';

/**
 * Maps legacy root redirects (`/`) to the app home route.
 * @param {string | undefined} redirectUrl - Redirect path or absolute URL from query params.
 * @returns {string | undefined} Normalized redirect, or undefined when input is undefined.
 */
export function normalizeOffice365RedirectPath(redirectUrl?: string): string | undefined {
  if (redirectUrl === undefined) {
    return undefined;
  }

  if (redirectUrl === '/') {
    return OFFICE_365_DEFAULT_REDIRECT;
  }

  return redirectUrl;
}

/**
 * Builds auth2 Azure login `redirect` query value as an absolute application URL.
 * @param {object} params - Redirect resolution inputs.
 * @param {string | undefined} params.redirectUrl - Path or absolute URL from the app.
 * @param {string} params.origin - Current window origin (e.g. https://devmybackoffice.qima.com).
 * @param {string | undefined} params.fallbackPath - Path used when redirectUrl is omitted.
 * @returns {string} Absolute redirect URL for auth2.
 */
export function toOffice365Auth2RedirectUrl(params: { readonly fallbackPath?: string; readonly origin: string; readonly redirectUrl?: string }): string {
  const { redirectUrl, origin, fallbackPath = OFFICE_365_DEFAULT_REDIRECT } = params;
  const target = normalizeOffice365RedirectPath(redirectUrl) ?? fallbackPath;

  if (/^https?:\/\//i.test(target)) {
    const url = new URL(target);

    if (url.pathname === '/' && !url.search && !url.hash) {
      return `${url.origin}${OFFICE_365_DEFAULT_REDIRECT}`;
    }

    return target;
  }

  const path = target.startsWith('/') ? target : `/${target}`;
  const resolvedPath = path === '/' ? OFFICE_365_DEFAULT_REDIRECT : path;

  return `${origin}${resolvedPath}`;
}

/**
 * Converts auth2 callback `redirect` (absolute URL or in-app path) to a router path.
 * @param {string} redirectUrl - Redirect from auth2 callback query params.
 * @param {string} origin - Current window origin.
 * @returns {string} In-app path for Router.navigateByUrl.
 */
export function toOffice365AppRedirectPath(redirectUrl: string, origin: string): string {
  const normalizedRedirect = normalizeOffice365RedirectPath(redirectUrl) ?? redirectUrl;

  if (!/^https?:\/\//i.test(normalizedRedirect)) {
    const path = normalizedRedirect.startsWith('/') ? normalizedRedirect : `/${normalizedRedirect}`;

    return path === '/' ? OFFICE_365_DEFAULT_REDIRECT : path;
  }

  const url = new URL(normalizedRedirect);

  if (url.origin === origin) {
    const path = `${url.pathname}${url.search}${url.hash}`;

    return path === '/' ? OFFICE_365_DEFAULT_REDIRECT : path;
  }

  return normalizedRedirect;
}
